import { useAuth } from '@/contexts/AuthContext';
import { usePermissions } from '@/hooks/usePermissions';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';

interface FeatureAccess {
  enabled: boolean;
  tierRequired?: string;
  upgradeMessage?: string;
}

interface OrganizationFeature {
  feature: string;
  enabled: boolean;
  tier: string;
}

/**
 * Hook to check if a user has access to a feature based on:
 * 1. Role-based permissions (admin/manager/cook/viewer)
 * 2. Organization billing state (active/past_due/suspended)
 * 3. Subscription tier features (Foundation/Professional/Enterprise)
 * 
 * @example
 * const { canAccess, loading } = useFeatureAccess();
 * const access = canAccess('advanced_reports', 'read');
 * if (!access.enabled) {
 *   return <UpgradePrompt message={access.upgradeMessage} />;
 * }
 */
export const useFeatureAccess = () => {
  const { can, loading: permissionsLoading } = usePermissions();
  const { user } = useAuth();
  const org = user?.organization;

  // Fetch organization's effective features (tier defaults + overrides)
  const { data: features, isLoading: featuresLoading } = useQuery<OrganizationFeature[]>({
    queryKey: ['organization-features', org?.id],
    queryFn: async () => {
      if (!org?.id) return [];

      const { data, error } = await supabase.rpc('get_org_features', {
        _org_id: org.id
      });

      if (error) {
        console.error('[useFeatureAccess] Failed to fetch org features:', error);
        return [];
      }

      return data as OrganizationFeature[];
    },
    enabled: !!org?.id,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  /**
   * Check if user can access a specific resource/action
   * Combines role permissions + tier features + billing state
   */
  const canAccess = (resource: string, action: string): FeatureAccess => {
    // Check 1: Role permission (from permissions table)
    const hasRolePermission = can(resource, action);
    
    // Check 2: Billing state
    const billingActive = org?.billing_state === 'active';
    
    // Check 3: Tier feature access
    const feature = features?.find(f => f.feature === resource);
    const hasTierAccess = feature?.enabled ?? false;

    // Return appropriate error messages
    if (!hasRolePermission) {
      return {
        enabled: false,
        upgradeMessage: `Your role does not have permission to ${action} ${resource}.`,
      };
    }

    if (!billingActive) {
      const stateMessage = org?.billing_state === 'past_due' 
        ? 'Your organization has a past due payment. Please update billing to continue.'
        : org?.billing_state === 'suspended'
        ? 'Your organization account is suspended. Please contact support.'
        : 'Your organization billing is not active. Please contact your admin.';
      
      return {
        enabled: false,
        upgradeMessage: stateMessage,
      };
    }

    if (!hasTierAccess) {
      const currentTier = org?.subscription_tier || 'None';
      const requiredTier = feature?.tier || 'Unknown';
      
      return {
        enabled: false,
        tierRequired: requiredTier,
        upgradeMessage: `This feature requires the ${requiredTier} plan. Your current plan: ${currentTier}.`,
      };
    }

    return { enabled: true };
  };

  return {
    canAccess,
    loading: permissionsLoading || featuresLoading,
    features,
  };
};
